// Fereastra plutitoare cu bitcoinul, peste orice filă a screener-ului.
//
// De ce e nevoie de ea: orice monedă se citește raportat la BTC, iar pentru asta trebuia
// părăsit graficul deschis. Fereastra stă deasupra, se trage de antet, se redimensionează
// din colțul de jos-dreapta și are propriul interval de timp.
//
// Poziția, mărimea și intervalul se rețin — o fereastră care sare în alt loc la fiecare
// reîncărcare e mai enervantă decât utilă.

import { el, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { settings } from '../core/state.js';
import { Chart } from '../chart/chart.js';

const KEY = 'sx_btc_widget';
const TFS = ['1m', '5m', '15m', '1h'];
const MIN_W = 240;
const MIN_H = 170;

const DEFAULTS = { on: false, x: null, y: null, w: 340, h: 230, tf: '5m' };

export function btcWidgetState() {
  return { ...DEFAULTS, ...(settings.get(KEY, null) || {}) };
}

export class BtcWidget {
  #state = btcWidgetState();
  #chart = null;
  #root = null;

  constructor(app) {
    this.app = app;
    bus.on('btcwidget:toggle', (on) => this.setVisible(on));
    if (this.#state.on) this.mount();
  }

  #save(patch) {
    this.#state = { ...this.#state, ...patch };
    settings.set(KEY, this.#state);
  }

  setVisible(on) {
    this.#save({ on });
    if (on) this.mount();
    else this.destroy();
  }

  mount() {
    if (this.#root) return;
    const s = this.#state;

    // Prima deschidere: colțul de jos-dreapta, la distanță de marginile ferestrei.
    const x = s.x ?? Math.max(12, window.innerWidth - s.w - 24);
    const y = s.y ?? Math.max(12, window.innerHeight - s.h - 32);

    this.tfEl = el('div', { class: 'btcw-tfs' }, TFS.map((tf) => el('button', {
      class: `btcw-tf${tf === s.tf ? ' active' : ''}`,
      text: tf.toUpperCase(),
      onclick: (e) => { e.stopPropagation(); this.#setTf(tf); },
    })));

    this.headEl = el('div', { class: 'btcw-head' }, [
      el('span', { class: 'btcw-title' }, [icon('bitcoin', 11), el('span', { text: 'BTC' })]),
      this.priceEl = el('span', { class: 'btcw-price', text: '—' }),
      this.tfEl,
      el('button', {
        class: 'btcw-close', title: 'Скрыть окно',
        onclick: () => this.setVisible(false),
      }, [icon('close', 11)]),
    ]);

    this.bodyEl = el('div', { class: 'btcw-body' });
    this.gripEl = el('div', { class: 'btcw-grip', title: 'Изменить размер' });

    this.#root = el('div', { class: 'btcw' }, [this.headEl, this.bodyEl, this.gripEl]);
    Object.assign(this.#root.style, {
      left: `${x}px`, top: `${y}px`, width: `${s.w}px`, height: `${s.h}px`,
    });
    document.body.append(this.#root);

    this.#dragBy(this.headEl, 'move');
    this.#dragBy(this.gripEl, 'resize');
    this.#openChart();
  }

  #openChart() {
    this.#chart?.destroy();
    this.#chart = new Chart(this.bodyEl, {
      exchange: this.app.exchange,
      market: this.app.market,
      symbol: 'BTCUSDT',
      tf: this.#state.tf,
      compact: true,
      limit: 200,
      indicators: { volume: false },
      showLevels: false,
    });
    this.#chart.load().then(() => this.#showPrice()).catch(() => {});
    // Prețul din antet urmărește ultima bară, ca să nu fie nevoie de citit de pe axă.
    this.offBar = bus.on('chart:bar', ({ symbol, bar }) => {
      if (symbol === 'BTCUSDT') this.#showPrice(bar);
    });
  }

  #showPrice(bar = null) {
    const last = bar || this.#chart?.bars?.[this.#chart.bars.length - 1];
    if (!last || !this.priceEl) return;
    this.priceEl.textContent = last.close.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
    this.priceEl.classList.toggle('up', last.close >= last.open);
    this.priceEl.classList.toggle('down', last.close < last.open);
  }

  #setTf(tf) {
    this.#save({ tf });
    for (const b of this.tfEl.children) b.classList.toggle('active', b.textContent === tf.toUpperCase());
    this.#chart?.setTimeframe(tf);
  }

  /**
   * Mutarea și redimensionarea, pe evenimente de pointer.
   *
   * `setPointerCapture` ține gestul chiar dacă mouse-ul iese de pe element sau trece
   * peste pânza graficului — fără el, fereastra rămâne agățată de cursor.
   */
  #dragBy(handle, mode) {
    handle.addEventListener('pointerdown', (ev) => {
      if (ev.target.closest('button')) return;
      ev.preventDefault();
      handle.setPointerCapture(ev.pointerId);
      const r = this.#root.getBoundingClientRect();
      const start = { x: ev.clientX, y: ev.clientY, left: r.left, top: r.top, w: r.width, h: r.height };

      const onMove = (e) => {
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (mode === 'move') {
          // Ținem fereastra în ecran: scoasă complet afară, n-ar mai putea fi prinsă.
          const left = Math.min(Math.max(0, start.left + dx), window.innerWidth - 80);
          const top = Math.min(Math.max(0, start.top + dy), window.innerHeight - 40);
          this.#root.style.left = `${left}px`;
          this.#root.style.top = `${top}px`;
        } else {
          const w = Math.max(MIN_W, start.w + dx);
          const h = Math.max(MIN_H, start.h + dy);
          this.#root.style.width = `${w}px`;
          this.#root.style.height = `${h}px`;
          this.#chart?.resize();
        }
      };

      const onUp = () => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        const r2 = this.#root.getBoundingClientRect();
        this.#save({ x: Math.round(r2.left), y: Math.round(r2.top), w: Math.round(r2.width), h: Math.round(r2.height) });
        if (mode === 'resize') this.#chart?.resize();
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
    });
  }

  /** Bursa activă s-a schimbat — graficul trebuie luat de la ea. */
  refreshSource() {
    if (!this.#root) return;
    this.#openChart();
  }

  destroy() {
    this.offBar?.();
    this.#chart?.destroy();
    this.#chart = null;
    this.#root?.remove();
    this.#root = null;
  }
}
